/*
 * delivery-estimate.js
 * ---------------------
 * Dynamický odhad termínu doručení na detailu produktu.
 *
 * Proč v JS a ne v Liquidu: Shopify cachuje storefront stránky, takže Liquid
 * `'now'` může "zamrznout" a ukazovat staré datum. Výpočet proto běží na klientu
 * a je ukotvený na časové pásmo Europe/Prague (nezávisle na TZ prohlížeče).
 *
 * Dva režimy (data-mode na elementu [data-delivery-estimate]):
 *   - "in-stock"  – sklad > 0, expedujeme průběžně (následující pracovní den)
 *                    → jedno konkrétní datum doručení.
 *   - "supplier"  – sklad ≤ 0, objednáváme u dodavatele v týdenním cyklu
 *                    (Út objednáme → Čt přijde → Pá expedujeme → Po/Út u vás)
 *                    → rozmezí po–út + poznámka o svátcích.
 *
 * Uzávěrka pro aktuální úterní objednávku = pondělí 14:00 (Europe/Prague)
 * (Út–Ne a Po před 14:00 → nejbližší úterý; Po od 14:00 → úterý o týden později).
 */
(function () {
  'use strict';

  var DAY_MS = 86400000;

  // --- Datum ukotvené na Europe/Prague, reprezentované jako UTC poledne -------
  // Pracujeme s "date-only" hodnotou zakotvenou na 12:00 UTC, takže veškerá
  // aritmetika po dnech je imunní vůči přechodům letního času.
  function pragueToday(refDate) {
    var base = refDate || new Date();
    var parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Prague',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(base);
    var y = +parts.find(function (p) { return p.type === 'year'; }).value;
    var m = +parts.find(function (p) { return p.type === 'month'; }).value;
    var d = +parts.find(function (p) { return p.type === 'day'; }).value;
    return new Date(Date.UTC(y, m - 1, d, 12));
  }

  // Minuty od půlnoci v Europe/Prague (0–1439) – kvůli uzávěrce v pondělí 14:00.
  function pragueMinutes(refDate) {
    var parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Prague',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(refDate || new Date());
    var h = +parts.find(function (p) { return p.type === 'hour'; }).value % 24;
    var min = +parts.find(function (p) { return p.type === 'minute'; }).value;
    return h * 60 + min;
  }

  function addDays(date, n) {
    return new Date(date.getTime() + n * DAY_MS);
  }

  function ymd(date) {
    var y = date.getUTCFullYear();
    var m = String(date.getUTCMonth() + 1).padStart(2, '0');
    var d = String(date.getUTCDate()).padStart(2, '0');
    return y + '-' + m + '-' + d;
  }

  // --- Státní svátky ČR (vč. pohyblivých Velikonoc) ---------------------------
  // Velikonoční neděle podle Meeus/Jones/Butcher (Anonymous Gregorian).
  function easterSunday(year) {
    var a = year % 19;
    var b = Math.floor(year / 100);
    var c = year % 100;
    var d = Math.floor(b / 4);
    var e = b % 4;
    var f = Math.floor((b + 8) / 25);
    var g = Math.floor((b - f + 1) / 3);
    var h = (19 * a + b - d - g + 15) % 30;
    var i = Math.floor(c / 4);
    var k = c % 4;
    var l = (32 + 2 * e + 2 * i - h - k) % 7;
    var mm = Math.floor((a + 11 * h + 22 * l) / 451);
    var month = Math.floor((h + l - 7 * mm + 114) / 31); // 3=březen, 4=duben
    var day = ((h + l - 7 * mm + 114) % 31) + 1;
    return new Date(Date.UTC(year, month - 1, day, 12));
  }

  var holidayCache = {};
  function czHolidays(year) {
    if (holidayCache[year]) return holidayCache[year];
    var set = {};
    var fixed = [
      [1, 1],   // Nový rok
      [5, 1],   // Svátek práce
      [5, 8],   // Den vítězství
      [7, 5],   // Cyril a Metoděj
      [7, 6],   // Jan Hus
      [9, 28],  // Sv. Václav
      [10, 28], // Vznik ČSR
      [11, 17], // Boj za svobodu a demokracii
      [12, 24], // Štědrý den
      [12, 25], // 1. svátek vánoční
      [12, 26], // 2. svátek vánoční
    ];
    fixed.forEach(function (md) {
      set[ymd(new Date(Date.UTC(year, md[0] - 1, md[1], 12)))] = true;
    });
    var easter = easterSunday(year);
    set[ymd(addDays(easter, -2))] = true; // Velký pátek
    set[ymd(addDays(easter, 1))] = true;  // Velikonoční pondělí
    holidayCache[year] = set;
    return set;
  }

  function isHoliday(date) {
    return !!czHolidays(date.getUTCFullYear())[ymd(date)];
  }

  function isBusinessDay(date) {
    var dow = date.getUTCDay(); // 0 = neděle, 6 = sobota
    return dow !== 0 && dow !== 6 && !isHoliday(date);
  }

  // Vrátí `date`, pokud je pracovní den, jinak nejbližší následující pracovní den.
  function ensureBusinessDay(date) {
    var d = date;
    while (!isBusinessDay(d)) d = addDays(d, 1);
    return d;
  }

  // Nejbližší pracovní den STRIKTNĚ po `date`.
  function nextBusinessDay(date) {
    var d = addDays(date, 1);
    while (!isBusinessDay(d)) d = addDays(d, 1);
    return d;
  }

  function addBusinessDays(date, n) {
    var d = date;
    for (var i = 0; i < n; i++) d = nextBusinessDay(d);
    return d;
  }

  // Nejbližší výskyt daného dne v týdnu (targetDow) v den `from` nebo po něm.
  function onOrAfterWeekday(from, targetDow) {
    var ahead = (targetDow - from.getUTCDay() + 7) % 7;
    return addDays(from, ahead);
  }

  // --- Výpočty odhadů ---------------------------------------------------------

  // Uzávěrka objednávek: pondělí (1) ve 14:00 Europe/Prague. Do té doby stihneme
  // objednávky zpracovat, aby šly do úterního objednání u dodavatele.
  var CUTOFF_DOW = 1;
  var CUTOFF_MINUTES = 14 * 60;

  // Dodavatelský cyklus. `minutes` = čas dne v Praze (pragueMinutes), bez něj
  // se počítá jako před uzávěrkou. Vrací { start, end, holidayInWindow }.
  function supplierEstimate(now, minutes) {
    // Objednávka jde do úterního objednání hned po nejbližší pondělní uzávěrce.
    // V pondělí od 14:00 už je ta dnešní pryč → až uzávěrka za týden.
    var cutoff = onOrAfterWeekday(now, CUTOFF_DOW);
    if (cutoff.getTime() === now.getTime() && (minutes || 0) >= CUTOFF_MINUTES) {
      cutoff = addDays(cutoff, 7);
    }
    var orderTue = addDays(cutoff, 1); // úterý po uzávěrce (Po + 1)

    var order = ensureBusinessDay(orderTue);              // Út objednáme
    var arrival = ensureBusinessDay(addDays(order, 2));   // Čt zboží přijde
    var ship = ensureBusinessDay(addDays(arrival, 1));    // Pá expedujeme
    var start = nextBusinessDay(ship);                    // Po u zákazníka
    var end = addBusinessDays(start, 1);                  // Út u zákazníka

    var holidayInWindow = false;
    for (var d = orderTue; d.getTime() <= end.getTime(); d = addDays(d, 1)) {
      if (isHoliday(d)) { holidayInWindow = true; break; }
    }

    return { start: start, end: end, holidayInWindow: holidayInWindow };
  }

  // Skladové zboží – expedice následující pracovní den. Vrací { date }.
  function inStockEstimate(now) {
    var dispatch = nextBusinessDay(now); // expedice
    var delivery = nextBusinessDay(dispatch); // doručení
    return { date: delivery };
  }

  // --- Formátování ------------------------------------------------------------
  var czFormatter;
  function formatCz(date) {
    if (!czFormatter) {
      czFormatter = new Intl.DateTimeFormat('cs-CZ', {
        timeZone: 'UTC',
        weekday: 'short',
        day: 'numeric',
        month: 'numeric',
      });
    }
    return czFormatter.format(date); // např. "po 14. 7."
  }

  // --- DOM --------------------------------------------------------------------
  function fillElement(el) {
    if (el.dataset.deFilled) return; // guard proti smyčce MutationObserveru
    var now = pragueToday();
    var mode = el.dataset.mode;

    if (mode === 'in-stock') {
      var est = inStockEstimate(now);
      var tpl = el.dataset.tpl || 'U vás __DATE__';
      el.textContent = tpl.replace('__DATE__', formatCz(est.date));
    } else if (mode === 'supplier') {
      var s = supplierEstimate(now, pragueMinutes());
      var rangeTpl = el.dataset.tpl || 'U vás __START__ – __END__';
      var rangeText = rangeTpl
        .replace('__START__', formatCz(s.start))
        .replace('__END__', formatCz(s.end));

      el.textContent = '';
      var range = document.createElement('span');
      range.className = 'product__delivery-estimate-range';
      range.textContent = rangeText;
      el.appendChild(range);

      // Poznámku o svátcích ukazujeme jen když svátek reálně padne do cyklu –
      // jinak by zbytečně zabírala místo (rozmezí je už tak posunuté správně).
      if (el.dataset.note && s.holidayInWindow) {
        var note = document.createElement('span');
        note.className = 'product__delivery-estimate-note';
        note.textContent = el.dataset.note;
        el.appendChild(note);
      }
    } else {
      return;
    }

    el.dataset.deFilled = '1';
    el.hidden = false;
  }

  function initDeliveryEstimates(root) {
    (root || document).querySelectorAll('[data-delivery-estimate]').forEach(fillElement);
  }

  // Při změně varianty product-info.js přepíše innerHTML #Inventory-{section}.
  // Nový element nemá data-deFilled → MutationObserver ho znovu naplní.
  function observeInventory() {
    document.querySelectorAll('[id^="Inventory-"]').forEach(function (inv) {
      var obs = new MutationObserver(function () {
        initDeliveryEstimates(inv);
      });
      obs.observe(inv, { childList: true, subtree: true });
    });
  }

  // „?" tooltip u dodavatelského termínu. Delegováno na document, takže
  // funguje i po přepsání #Inventory při změně varianty (bez re-bindingu).
  function closeAllHelp(except) {
    document.querySelectorAll('.product__delivery-help-popup').forEach(function (p) {
      if (p === except) return;
      p.hidden = true;
      var owner = p.parentNode.querySelector('.product__delivery-help-btn');
      if (owner) owner.setAttribute('aria-expanded', 'false');
    });
  }

  function initDeliveryHelp() {
    document.addEventListener('click', function (e) {
      var t = e.target;
      // Klik uvnitř bubliny (např. na odkaz) nic nezavírá.
      if (t.closest && t.closest('.product__delivery-help-popup')) return;
      var btn = t.closest && t.closest('.product__delivery-help-btn');
      var pop = btn ? btn.parentNode.querySelector('.product__delivery-help-popup') : null;
      closeAllHelp(pop);
      if (btn && pop) {
        e.stopPropagation();
        var willOpen = pop.hidden;
        pop.hidden = !willOpen;
        btn.setAttribute('aria-expanded', String(willOpen));
      }
    });
  }

  if (typeof document !== 'undefined') {
    var boot = function () {
      initDeliveryEstimates(document);
      observeInventory();
      initDeliveryHelp();
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }

  // Export pro Node unit testy.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      pragueToday: pragueToday,
      pragueMinutes: pragueMinutes,
      czHolidays: czHolidays,
      isBusinessDay: isBusinessDay,
      nextBusinessDay: nextBusinessDay,
      addBusinessDays: addBusinessDays,
      supplierEstimate: supplierEstimate,
      inStockEstimate: inStockEstimate,
      formatCz: formatCz,
      easterSunday: easterSunday,
      fillElement: fillElement,
      initDeliveryEstimates: initDeliveryEstimates,
    };
  }
})();
